import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { siteSwitches } from "../../api/endpoints/siteSwitches";
import { qk } from "../../api/queryKeys";
import type { SwitchOut } from "../../api/types/siteSwitch";
import { Table, type Column } from "../../components/ui/Table";
import { EmptyState } from "../../components/ui/EmptyState";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { Modal } from "../../components/ui/Modal";
import { SwitchForm } from "./SwitchForm";
import { SwitchPortMap } from "./SwitchPortMap";
import { WebUiInstructionsPanel } from "./WebUiInstructionsPanel";
import { useVlanTemplateOptions } from "./useVlanTemplateOptions";
import { getManagementMode } from "./managementMode";

export function SwitchesSection({
  siteSlug,
  canWrite,
}: {
  siteSlug: string;
  canWrite: boolean;
}) {
  const qc = useQueryClient();
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [editing, setEditing] = useState<SwitchOut | null>(null);
  const [adding, setAdding] = useState(false);

  const q = useQuery({
    queryKey: qk.siteSwitches(siteSlug),
    queryFn: () => siteSwitches.list(siteSlug).then((r) => r.data),
  });

  const del = useMutation({
    mutationFn: (slug: string) => siteSwitches.remove(siteSlug, slug),
    onSuccess: (_d, slug) => {
      void qc.invalidateQueries({ queryKey: qk.siteSwitches(siteSlug) });
      if (selectedSlug === slug) setSelectedSlug(null);
    },
  });

  const columns: ReadonlyArray<Column<SwitchOut>> = [
    { key: "slug", header: "Slug", render: (s) => <code>{s.slug}</code> },
    { key: "identity", header: "Identity", render: (s) => s.identity ?? "—" },
    { key: "model", header: "Model", render: (s) => s.model ?? "—" },
    { key: "mode", header: "Mode", render: (s) => <ModeBadge sw={s} /> },
    { key: "role", header: "Role", render: (s) => s.role ?? "—" },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (s) =>
        canWrite ? (
          <span style={{ display: "inline-flex", gap: 8 }}>
            <button type="button" style={linkBtn} onClick={(e) => { e.stopPropagation(); setEditing(s); }}>Edit</button>
            <button
              type="button"
              style={dangerLinkBtn}
              disabled={del.isPending}
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`Delete switch ${s.slug}?`)) del.mutate(s.slug);
              }}
            >
              Delete
            </button>
          </span>
        ) : null,
    },
  ];

  if (q.isPending) return <p style={{ color: "var(--text-dim)" }}>Loading switches…</p>;
  if (q.error) return <ErrorBanner error={q.error} title="Failed to load switches" />;

  const switches = [...(q.data ?? [])].sort((a, b) => a.slug.localeCompare(b.slug));
  const selected = switches.find((s) => s.slug === selectedSlug) ?? null;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0, fontSize: "0.95rem" }}>Switches</h3>
        {canWrite && (
          <button type="button" style={primaryBtn} onClick={() => setAdding(true)}>Add switch</button>
        )}
      </div>

      {del.error && <ErrorBanner error={del.error} title="Delete failed" />}

      <Table<SwitchOut>
        columns={columns}
        rows={switches}
        rowKey={(s) => s.slug}
        onRowClick={(s) => setSelectedSlug(s.slug)}
        empty={<EmptyState title="No switches" description="Add the site's managed switches (CLI or web-UI)." />}
      />

      {selected && <SwitchDetail key={selected.slug} siteSlug={siteSlug} sw={selected} canWrite={canWrite} />}

      <Modal
        open={adding || editing !== null}
        onClose={() => { setAdding(false); setEditing(null); }}
        title={editing ? `Edit switch ${editing.slug}` : "Add switch"}
        width={640}
      >
        <SwitchForm
          siteSlug={siteSlug}
          sw={editing ?? undefined}
          canWrite={canWrite}
          onClose={() => { setAdding(false); setEditing(null); }}
        />
      </Modal>
    </div>
  );
}

// Single owner of the selected switch's ports query + VLAN template options;
// feeds both the port map and (web_ui) the instructions panel.
function SwitchDetail({
  siteSlug,
  sw,
  canWrite,
}: {
  siteSlug: string;
  sw: SwitchOut;
  canWrite: boolean;
}) {
  const portsQ = useQuery({
    queryKey: qk.siteSwitchPorts(siteSlug, sw.slug),
    queryFn: () => siteSwitches.listPorts(siteSlug, sw.slug).then((r) => r.data),
  });
  const vlan = useVlanTemplateOptions();
  const mode = getManagementMode(sw.vendor_extras);
  const ports = portsQ.data ?? [];

  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 16, display: "grid", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <strong>{sw.identity || sw.slug}</strong>
        <ModeBadge sw={sw} />
        {sw.model && <span style={{ color: "var(--text-dim)", fontSize: "0.85rem" }}>{sw.model}</span>}
      </div>

      {portsQ.isPending || vlan.isPending ? (
        <p style={{ color: "var(--text-dim)" }}>Loading ports + VLAN template…</p>
      ) : (portsQ.error ?? vlan.error) ? (
        // Gate on BOTH queries — an empty VLAN template would silently strip every
        // port/native/tagged selector choice and degrade the instructions.
        <ErrorBanner error={portsQ.error ?? vlan.error} title="Failed to load switch detail" />
      ) : (
        <>
          <SwitchPortMap
            siteSlug={siteSlug}
            switchSlug={sw.slug}
            ports={ports}
            options={vlan.options}
            nameByTag={vlan.nameByTag}
            canWrite={canWrite}
          />
          {mode === "web_ui" ? (
            <WebUiInstructionsPanel sw={sw} ports={ports} nameByTag={vlan.nameByTag} />
          ) : (
            <p style={{ color: "var(--text-dim)", fontSize: "0.85rem" }}>
              CLI-managed — Hub will apply this config in a later phase (C.5).
            </p>
          )}
        </>
      )}
    </div>
  );
}

function ModeBadge({ sw }: { sw: SwitchOut }) {
  const web = getManagementMode(sw.vendor_extras) === "web_ui";
  return (
    <span
      style={{
        fontSize: "0.72rem",
        padding: "2px 6px",
        borderRadius: 4,
        border: "1px solid var(--border)",
        color: "var(--text-dim)",
        whiteSpace: "nowrap",
      }}
    >
      {web ? "Web UI" : "CLI"}
    </span>
  );
}

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)", color: "var(--bg)", border: "none",
  padding: "6px 12px", borderRadius: 4, fontSize: "0.85rem", fontWeight: 600, cursor: "pointer",
};
const linkBtn: React.CSSProperties = {
  background: "transparent", border: "none", color: "var(--accent)", cursor: "pointer", fontSize: "0.85rem", padding: 0,
};
const dangerLinkBtn: React.CSSProperties = { ...linkBtn, color: "var(--crit)" };
